import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { SKU } from '@festpix/types';

export interface CreateSKUParams {
  name: string;
  shortName: string;
  price: number;
  quantity: number;
  controlInventory: boolean;
}

export interface UpdateSKUParams {
  name?: string;
  shortName?: string;
  price?: number;
  quantity?: number;
  controlInventory?: boolean;
}

export const MAX_SKUS_PER_BOOTH = 6;
export const MAX_SHORT_NAME_LENGTH = 15;

/**
 * Get all SKUs for a specific booth
 */
export async function getBoothSKUs(boothId: string): Promise<SKU[]> {
  const skusQuery = query(
    collection(db, 'skus'),
    where('boothId', '==', boothId),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(skusQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      boothId: data.boothId,
      name: data.name,
      shortName: data.shortName,
      price: data.price,
      quantity: data.quantity,
      controlInventory: data.controlInventory ?? true,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    };
  }) as SKU[];
}

/**
 * Create a new SKU for a booth
 */
export async function createSKU(boothId: string, params: CreateSKUParams): Promise<SKU> {
  const { name, shortName, price, quantity, controlInventory } = params;

  // Validate shortName length
  if (shortName.length > MAX_SHORT_NAME_LENGTH) {
    throw new Error(`Nome curto deve ter no máximo ${MAX_SHORT_NAME_LENGTH} caracteres`);
  }

  // Validate price
  if (price <= 0) {
    throw new Error('Preço deve ser maior que zero');
  }

  // Validate quantity only if inventory control is enabled
  if (controlInventory && quantity < 0) {
    throw new Error('Quantidade não pode ser negativa');
  }

  // Check existing SKUs count for this booth
  const existingSKUs = await getBoothSKUs(boothId);
  if (existingSKUs.length >= MAX_SKUS_PER_BOOTH) {
    throw new Error(`Máximo de ${MAX_SKUS_PER_BOOTH} SKUs por estande`);
  }

  const skuRef = await addDoc(collection(db, 'skus'), {
    boothId,
    name: name.trim(),
    shortName: shortName.trim(),
    price,
    quantity: controlInventory ? quantity : 0,
    controlInventory,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: skuRef.id,
    boothId,
    name: name.trim(),
    shortName: shortName.trim(),
    price,
    quantity: controlInventory ? quantity : 0,
    controlInventory,
  };
}

/**
 * Update an existing SKU
 */
export async function updateSKU(skuId: string, params: UpdateSKUParams): Promise<void> {
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (params.name !== undefined) {
    updates.name = params.name.trim();
  }

  if (params.shortName !== undefined) {
    if (params.shortName.length > MAX_SHORT_NAME_LENGTH) {
      throw new Error(`Nome curto deve ter no máximo ${MAX_SHORT_NAME_LENGTH} caracteres`);
    }
    updates.shortName = params.shortName.trim();
  }

  if (params.price !== undefined) {
    if (params.price <= 0) {
      throw new Error('Preço deve ser maior que zero');
    }
    updates.price = params.price;
  }

  if (params.controlInventory !== undefined) {
    updates.controlInventory = params.controlInventory;
  }

  if (params.quantity !== undefined) {
    // Only validate quantity if controlInventory is being set to true or already true
    if (params.controlInventory === true && params.quantity < 0) {
      throw new Error('Quantidade não pode ser negativa');
    }
    updates.quantity = params.quantity;
  }

  await updateDoc(doc(db, 'skus', skuId), updates);
}

/**
 * Delete a SKU
 */
export async function deleteSKU(skuId: string): Promise<void> {
  await deleteDoc(doc(db, 'skus', skuId));
}

/**
 * Get all SKUs grouped by booth (for menu view)
 */
export async function getAllSKUsGroupedByBooth(): Promise<Map<string, { boothName: string; skus: SKU[] }>> {
  // Get all booths first
  const boothsSnapshot = await getDocs(collection(db, 'booths'));
  const boothsMap = new Map<string, string>();

  boothsSnapshot.docs.forEach((doc) => {
    boothsMap.set(doc.id, doc.data().name);
  });

  // Get all SKUs
  const skusSnapshot = await getDocs(query(collection(db, 'skus'), orderBy('name', 'asc')));

  const result = new Map<string, { boothName: string; skus: SKU[] }>();

  skusSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    const boothId = data.boothId;
    const boothName = boothsMap.get(boothId) || 'Estande Desconhecido';

    if (!result.has(boothId)) {
      result.set(boothId, { boothName, skus: [] });
    }

    result.get(boothId)!.skus.push({
      id: doc.id,
      boothId,
      name: data.name,
      shortName: data.shortName,
      price: data.price,
      quantity: data.quantity,
      controlInventory: data.controlInventory ?? true,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    });
  });

  return result;
}

export type { SKU };
